import { useEffect, useRef, useState } from "react";

interface Props {
  videoId: string;
  /** Start time in seconds. */
  start?: number;
  /** End time in seconds — playback resets to `start` when reached. */
  end?: number;
  className?: string;
  title?: string;
}

/**
 * Vimeo background video som loopar segmentet [start, end].
 *
 * Prestanda: iframen och Vimeo Player-SDK laddas INTE förrän sidan är
 * interaktiv (efter `load`-event + `requestIdleCallback`). Det gör att
 * hero-textens LCP inte blockeras av videons nätverkstrafik.
 */
export function VimeoLoop({
  videoId,
  start = 0,
  end,
  className,
  title = "Background video",
}: Props) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [ready, setReady] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);

  // Bestäm om videon ska laddas alls — hoppa över på mobil, save-data
  // och långsamma nät. Sparar 5+ MB där det behövs mest.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const nav = navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    };
    const conn = nav.connection;
    if (conn?.saveData) return;
    if (conn?.effectiveType && /(^|-)(2g|slow-2g|3g)$/i.test(conn.effectiveType)) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (window.matchMedia("(max-width: 767px)").matches) return;
    setShouldLoad(true);
  }, []);

  // Defer iframe mount until after the page is interactive.
  useEffect(() => {
    if (!shouldLoad || typeof window === "undefined") return;

    let cancelled = false;
    const schedule = () => {
      if (cancelled) return;
      const idle =
        (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number })
          .requestIdleCallback;
      if (idle) {
        idle(() => !cancelled && setReady(true), { timeout: 1500 });
      } else {
        window.setTimeout(() => !cancelled && setReady(true), 800);
      }
    };

    if (document.readyState === "complete") {
      schedule();
    } else {
      window.addEventListener("load", schedule, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener("load", schedule);
    };
  }, [shouldLoad]);

  useEffect(() => {
    if (!ready || !iframeRef.current) return;
    let disposed = false;
    let player: import("@vimeo/player").default | null = null;

    import("@vimeo/player").then(({ default: Player }) => {
      if (disposed || !iframeRef.current) return;
      player = new Player(iframeRef.current);

      // Tvinga låg upplösning — sparar flera MB jämfört med adaptivt val.
      player.setQuality?.("540p").catch(() => {});
      player.setMuted(true).catch(() => {});
      player.setVolume(0).catch(() => {});
      player.setCurrentTime(start).catch(() => {});
      player.play().catch(() => {});

      const onTime = ({ seconds }: { seconds: number }) => {
        if (end != null && seconds >= end) {
          player?.setCurrentTime(start).catch(() => {});
        }
      };
      const onEnded = () => {
        player?.setCurrentTime(start).catch(() => {});
        player?.play().catch(() => {});
      };

      player.on("timeupdate", onTime);
      player.on("ended", onEnded);
    });

    return () => {
      disposed = true;
      player?.destroy().catch(() => {});
    };
  }, [ready, start, end]);

  if (!ready) return null;

  const src = `https://player.vimeo.com/video/${videoId}?background=1&autoplay=1&muted=1&autopause=0&controls=0&title=0&byline=0&portrait=0&dnt=1&quality=540p#t=${start}s`;

  return (
    <iframe
      ref={iframeRef}
      src={src}
      title={title}
      allow="autoplay; fullscreen; picture-in-picture"
      loading="lazy"
      frameBorder={0}
      className={className}
    />
  );
}

